import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FitTextDirective } from './fit-text.directive';
import { collectDeviceEvidence } from '../../../utils/device-evidence';
import { HttpErrorResponse } from '@angular/common/http';
import type {
  MitMandateStatusValue,
  PublicMitChargeDTO,
  PublicMitEnrollmentResultDTO,
  PublicMitMandateDTO,
  PublicMitSavedCardDTO
} from '../../../core/models';
import {
  MIT_LIMIT_ROWS,
  MIT_MANDATE_STATUS_LABELS,
  MitLimitRow,
  MitService,
  mitApiError,
  mitCardRegistered,
  mitLimitValue
} from '../../../services/mit.service';

type EnrollView = 'loading' | 'invalid' | 'unavailable' | 'form' | 'receipt' | 'review';

interface DeviceInfo {
  userAgent: string;
  os: string;
  type: string;
}

function describeDevice(nav: Navigator | null): DeviceInfo {
  const ua = nav?.userAgent ?? '';
  const ipadOs = /Macintosh/.test(ua) && (nav?.maxTouchPoints ?? 0) > 1;
  const os = /Windows/.test(ua) ? 'Windows'
    : /iPhone|iPad|iPod/.test(ua) || ipadOs ? 'iOS'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android'
    : /CrOS/.test(ua) ? 'ChromeOS'
    : /Linux/.test(ua) ? 'Linux'
    : 'Sistema desconocido';
  const type = /iPad|Tablet/.test(ua) || ipadOs || (/Android/.test(ua) && !/Mobile/.test(ua)) ? 'Tableta'
    : /Mobi|iPhone|iPod|Android/.test(ua) ? 'Celular'
    : 'Computadora';
  return { userAgent: ua, os, type };
}

/** La tarjeta vale hasta el último día de su mes de vencimiento; el backend aplica la misma regla en hora de Ciudad de México. */
const cardNotExpired: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const month = Number(group.get('expMonth')?.value);
  const year = String(group.get('expYear')?.value ?? '');
  if (!(month >= 1 && month <= 12) || !/^\d{2}$/.test(year)) return null;
  const now = new Date();
  const current = now.getFullYear() * 12 + now.getMonth() + 1;
  return (2000 + Number(year)) * 12 + month < current ? { cardExpired: true } : null;
};

interface Clause {
  text: string;
  items: string[];
}

interface ReceiptCharge extends PublicMitChargeDTO {
  initial: boolean;
}

const STAMPS: Record<MitMandateStatusValue, { label: string; tone: string }> = {
  PENDING_ENROLLMENT: { label: 'PENDIENTE', tone: 'amber' },
  ACTIVE: { label: 'VIGENTE', tone: 'green' },
  SUSPENDED: { label: 'SUSPENDIDO', tone: 'amber' },
  REVOKED: { label: 'REVOCADO', tone: 'red' },
  EXPIRED: { label: 'VENCIDO', tone: 'gray' }
};

/**
 * Mandato para registrar (o reemplazar) la tarjeta con firma electrónica y, una vez firmado, su comprobante.
 * Nada de la tarjeta se guarda en el navegador.
 */
@Component({
  selector: 'app-public-mit-enroll',
  templateUrl: './public-mit-enroll.component.html',
  styleUrl: './public-mit-enroll.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FitTextDirective],
  host: { '(document:keydown.escape)': 'closeDeleteCard()' }
})
export class PublicMitEnrollComponent {
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private mitService = inject(MitService);

  private id = Number(this.route.snapshot.paramMap.get('id'));
  private hash = this.route.snapshot.queryParamMap.get('hash');

  readonly today = new Date();
  /** User-Agent que el navegador envía y el backend guarda con la firma, más el sistema y el tipo de equipo deducidos de él. */
  readonly device = describeDevice(typeof navigator !== 'undefined' ? navigator : null);

  view = signal<EnrollView>('loading');
  mandate = signal<PublicMitMandateDTO | null>(null);
  result = signal<PublicMitEnrollmentResultDTO | null>(null);
  sending = signal(false);
  error = signal<string | null>(null);
  attemptsExhausted = signal(false);
  savedCards = signal<PublicMitSavedCardDTO[]>([]);
  /** Tarjeta guardada elegida para firmar; `null` = capturar otra tarjeta. */
  selectedCardId = signal<number | null>(null);
  selectedCard = computed(() => this.savedCards().find((card) => card.id === this.selectedCardId()) ?? null);
  hasSelectableCards = computed(() => this.savedCards().some((card) => card.selectable));
  deletingCardId = signal<number | null>(null);
  cardToDelete = signal<PublicMitSavedCardDTO | null>(null);
  deleteCardError = signal<string | null>(null);
  cardLimitReached = signal(false);
  /** Correo capturado en la firma recién hecha; vacío al reabrir un mandato ya firmado. */
  signedEmail = signal<string | null>(null);

  isReplacement = computed(() => this.mandate()?.enrollmentPurpose === 'CARD_REPLACEMENT');
  /** El registro hace un cargo de validación que se cancela, no un primer cobro con venta. */
  isVerification = computed(() => this.mandate()?.enrollmentChargeType !== 'INITIAL_CHARGE');
  maxPerCharge = computed(() => this.mandate()?.limits?.maxAmountPerTransaction ?? 0);
  maxPerMonth = computed(() => this.mandate()?.limits?.maxAmountPerMonth ?? 0);
  /** Límites de la sección 02 sin los montos máximos y mínimos (esos quedan en cláusulas y aceptación). */
  limitRows = computed<MitLimitRow[]>(() => {
    const limits = this.mandate()?.limits;
    return MIT_LIMIT_ROWS.filter((row) => row.kind !== 'amount' && row.key !== 'maxConsecutiveDeclines' && limits?.[row.key] != null);
  });
  statusLabel = computed(() => {
    const status = this.mandate()?.status;
    return status ? MIT_MANDATE_STATUS_LABELS[status] : '';
  });
  stamp = computed(() => STAMPS[this.mandate()?.status ?? 'ACTIVE']);
  /** Cláusulas a partir del texto de términos que la API registra en el consentimiento. */
  clauses = computed<Clause[]>(() =>
    (this.mandate()?.termsText ?? '')
      .split(/\n\s*\n/)
      .map((block) => block.split('\n').map((line) => line.trim()).filter(Boolean))
      .filter((lines) => lines.length > 0)
      .map((lines) => ({
        text: lines.filter((line) => !line.startsWith('- ')).join(' '),
        items: lines.filter((line) => line.startsWith('- ')).map((line) => line.slice(2))
      }))
  );
  receiptCharges = computed<ReceiptCharge[]>(() => {
    const m = this.mandate();
    const charges: ReceiptCharge[] = (m?.recentCharges ?? []).map((charge) => ({ ...charge, initial: false }));
    if (m?.enrollmentCharge) charges.push({ ...m.enrollmentCharge, initial: true });
    return charges;
  });
  monthAvailable = computed(() => Math.max(0, this.maxPerMonth() - (this.mandate()?.monthUsedAmount ?? 0)));

  form = this.fb.group({
    customerName: ['', [Validators.required, Validators.maxLength(60)]],
    customerMiddleName: ['', [Validators.required, Validators.maxLength(60)]],
    customerLastName: ['', Validators.maxLength(60)],
    customerEmail: ['', [Validators.required, Validators.email]],
    customerPhone: ['', Validators.pattern(/^\d{10}$/)],
    cardNumber: ['', [Validators.required, Validators.pattern(/^\d{13,19}$/)]],
    expMonth: ['', [Validators.required, Validators.pattern(/^(0?[1-9]|1[0-2])$/)]],
    expYear: ['', [Validators.required, Validators.pattern(/^\d{2}$/)]],
    cvv: ['', [Validators.required, Validators.pattern(/^\d{3,4}$/)]],
    signatureName: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(150)]],
    consentAccepted: [false, Validators.requiredTrue]
  }, { validators: cardNotExpired });

  constructor() {
    if (!this.id || !this.hash) {
      this.view.set('invalid');
      return;
    }
    this.load();
  }

  limitValue(row: MitLimitRow): string {
    return mitLimitValue(row, this.mandate()?.limits);
  }

  canSign(): boolean {
    const signature = String(this.form.controls.signatureName.value ?? '').trim();
    return !this.sending() && !this.attemptsExhausted() && !this.holderIncomplete()
      && this.form.controls.consentAccepted.value === true && signature.length >= 4;
  }

  holderName(): string {
    const { customerName, customerMiddleName, customerLastName } = this.form.controls;
    return [customerName.value, customerMiddleName.value, customerLastName.value]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' ');
  }

  /** Los datos del titular los captura el comercio; si faltan, el titular no puede corregirlos aquí. */
  holderIncomplete(): boolean {
    const { customerName, customerMiddleName, customerLastName, customerEmail, customerPhone } = this.form.controls;
    return [customerName, customerMiddleName, customerLastName, customerEmail, customerPhone].some((control) => control.invalid);
  }

  /** Con una tarjeta guardada no se piden número, vigencia ni CVV. */
  selectCard(card: PublicMitSavedCardDTO | null): void {
    if (card && !card.selectable) return;
    this.selectedCardId.set(card?.id ?? null);
    const cardControls = [this.form.controls.cardNumber, this.form.controls.expMonth, this.form.controls.expYear, this.form.controls.cvv];
    if (card) {
      this.clearCard();
      cardControls.forEach((control) => control.disable());
    } else {
      cardControls.forEach((control) => control.enable());
    }
    if (this.cardLimitReached() && card) {
      this.cardLimitReached.set(false);
      this.error.set(null);
    }
  }

  onDigits(control: 'cardNumber' | 'expMonth' | 'expYear' | 'cvv', max: number): void {
    const ctrl = this.form.controls[control];
    const digits = String(ctrl.value ?? '').replace(/\D/g, '').slice(0, max);
    if (digits !== ctrl.value) ctrl.setValue(digits);
  }

  invalid(name: keyof typeof this.form.controls): boolean {
    const control = this.form.controls[name];
    return control.invalid && control.touched;
  }

  cardExpired(): boolean {
    const { expMonth, expYear } = this.form.controls;
    return this.form.hasError('cardExpired') && (expMonth.touched || expYear.touched);
  }

  print(): void {
    window.print();
  }

  async submit(): Promise<void> {
    const mandate = this.mandate();
    this.form.markAllAsTouched();
    if (!mandate || this.form.invalid || !this.canSign()) return;
    const raw = this.form.getRawValue();
    this.sending.set(true);
    this.error.set(null);
    this.signedEmail.set(raw.customerEmail!.trim());
    const device = await collectDeviceEvidence();
    const savedCardId = this.selectedCardId();
    const card = savedCardId != null
      ? { savedCardId }
      : { cardNumber: raw.cardNumber!, expMonth: raw.expMonth!.padStart(2, '0'), expYear: `20${raw.expYear}`, cvv: raw.cvv! };
    this.mitService.enrollPublic(this.id, this.hash!, {
      customerName: raw.customerName!.trim(),
      customerMiddleName: raw.customerMiddleName!.trim(),
      customerLastName: raw.customerLastName?.trim() || undefined,
      customerEmail: raw.customerEmail!.trim(),
      customerPhone: raw.customerPhone || undefined,
      ...card,
      consentAccepted: raw.consentAccepted === true,
      termsVersion: mandate.termsVersion ?? '',
      signatureName: raw.signatureName!.trim(),
      device
    }).subscribe({
      next: (response) => {
        this.sending.set(false);
        this.clearCard();
        this.onResult(response.body ?? null, response.message);
      },
      error: (err: HttpErrorResponse) => {
        this.sending.set(false);
        this.clearCard();
        this.onError(err);
      }
    });
  }

  private onResult(result: PublicMitEnrollmentResultDTO | null, message?: string): void {
    this.result.set(result);
    const status = result?.transactionStatus;
    if (mitCardRegistered(status)) {
      if (result?.mandate) this.mandate.set(result.mandate);
      this.view.set('receipt');
      window.scrollTo({ top: 0 });
      return;
    }
    if (status === 'PROCESSING' || status === 'PENDING_RECONCILIATION') {
      this.view.set('review');
      return;
    }
    const exhausted = result?.remainingAttempts === 0 || result?.mandateStatus === 'EXPIRED';
    this.attemptsExhausted.set(exhausted);
    this.error.set(result?.message || message || 'No se pudo completar el registro.');
  }

  private onError(err: HttpErrorResponse): void {
    if (err.status === 403) {
      this.view.set('invalid');
      return;
    }
    const { message, code } = mitApiError(err, 'No se pudo completar el registro. Intenta de nuevo.');
    if (code === 'ENROLLMENT_IN_PROGRESS') {
      this.view.set('review');
      return;
    }
    if (code === 'ENROLLMENT_NOT_AVAILABLE') {
      this.load();
      return;
    }
    if (code === 'CARD_LIMIT_REACHED') {
      this.cardLimitReached.set(true);
      this.error.set(`${message}. Elimina una tarjeta que ya no uses en «Tarjetas guardadas» e intenta de nuevo.`);
      this.loadSavedCards();
      document.getElementById('mit-saved-cards')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (code === 'SAVED_CARD_NOT_AVAILABLE') {
      this.selectCard(null);
      this.loadSavedCards();
      this.error.set(message);
      return;
    }
    if (code === 'TOO_MANY_ENROLLMENT_ATTEMPTS') {
      this.error.set('Hubo demasiados intentos de registro desde tu conexión. Espera una hora e intenta de nuevo.');
      return;
    }
    if (code === 'TERMS_VERSION_MISMATCH') {
      this.form.controls.consentAccepted.setValue(false);
      this.load();
    }
    if (err.status === 0) {
      this.view.set('review');
      return;
    }
    this.error.set(message);
  }

  private clearCard(): void {
    this.form.patchValue({ cardNumber: '', expMonth: '', expYear: '', cvv: '' });
    this.form.controls.cardNumber.markAsUntouched();
    this.form.controls.expMonth.markAsUntouched();
    this.form.controls.expYear.markAsUntouched();
    this.form.controls.cvv.markAsUntouched();
  }

  /** Datos del cliente en el comercio; el titular solo los ve, no los edita. */
  private prefill(mandate: PublicMitMandateDTO): void {
    const phone = (mandate.customerPhone ?? '').replace(/\D/g, '').slice(-10);
    this.form.patchValue({
      customerName: mandate.customerGivenNames?.trim() ?? '',
      customerMiddleName: mandate.customerPaternalName?.trim() ?? '',
      customerLastName: mandate.customerMaternalName?.trim() ?? '',
      customerEmail: mandate.customerEmail?.trim() ?? '',
      customerPhone: phone.length === 10 ? phone : ''
    });
  }

  /** Tarjetas guardadas del cliente con este comercio; solo él decide retirarlas. */
  loadSavedCards(): void {
    this.mitService.savedCardsPublic(this.id, this.hash!).subscribe({
      next: (response) => this.savedCards.set(response.body ?? []),
      error: () => this.savedCards.set([])
    });
  }

  openDeleteCard(card: PublicMitSavedCardDTO): void {
    if (card.inUse || this.deletingCardId() != null) return;
    this.deleteCardError.set(null);
    this.cardToDelete.set(card);
  }

  closeDeleteCard(): void {
    if (this.deletingCardId() != null) return;
    this.cardToDelete.set(null);
  }

  deleteSavedCard(): void {
    const card = this.cardToDelete();
    if (!card || this.deletingCardId() != null) return;
    this.deletingCardId.set(card.id);
    this.deleteCardError.set(null);
    this.mitService.deleteSavedCardPublic(this.id, this.hash!, card.id).subscribe({
      next: (response) => {
        this.deletingCardId.set(null);
        this.cardToDelete.set(null);
        this.savedCards.set(response.body ?? []);
        if (this.selectedCardId() === card.id) this.selectCard(null);
        if (this.cardLimitReached()) {
          this.cardLimitReached.set(false);
          this.error.set(null);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.deletingCardId.set(null);
        this.deleteCardError.set(mitApiError(err, 'No se pudo eliminar la tarjeta').message);
      }
    });
  }

  private load(): void {
    this.mitService.getPublic(this.id, this.hash!).subscribe({
      next: (response) => {
        const mandate = response.body ?? null;
        this.mandate.set(mandate);
        if (!mandate) {
          this.view.set('invalid');
          return;
        }
        if (mandate.enrollmentAvailable) {
          this.prefill(mandate);
          this.loadSavedCards();
        }
        this.view.set(mandate.enrollmentAvailable ? 'form' : mandate.signature ? 'receipt' : 'unavailable');
      },
      error: () => this.view.set('invalid')
    });
  }
}
